// Step2CarrierDetails.jsx
//
// Wizard step 2: carrier credentials (account number, transfer PIN, billing
// ZIP) for each portable number.
//
// Carrier-aware help text comes from the wizard's in-memory `carrierByPhone`
// map (populated by Step 1's portability-check). When the user re-enters an
// existing draft and this map is empty, we re-hydrate via `lookup-carrier` per
// number on mount. The lookup result is cached in the wizard-runtime state, so
// re-renders never re-hit Twilio Lookup.
//
// Phase F: once a carrier key is in hand, each card calls
// `lookup-carrier-metadata` to enrich the in-memory meta with screenshotPath,
// pinResetShortcut, accountNumberDigitCount, and commonMistakes. The rich
// coaching panel (screenshot with graceful "Visual coming soon" fallback,
// PIN-reset one-click action, inline common-mistake tips, digit-count
// soft-warn on the account-number field) renders only when those fields are
// present so Step 2 stays functional against the Phase B response shape.
//
// PIN + account number travel as plaintext on the wire to update-draft. The
// edge function encrypts them into Supabase Vault and the response strips the
// vault IDs, returning `account_pin_set` / `account_number_set` booleans we
// use to show a "Saved" affordance instead of an empty input.

import React, { useEffect, useRef, useState } from 'react';
import {
  KeyRound,
  Hash,
  MapPin,
  Eye,
  EyeOff,
  Info,
  Phone as PhoneIcon,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Lightbulb,
} from 'lucide-react';
import { lookupCarrier, lookupCarrierMetadata } from '../../../../../../lib/portInClient';

function formatForDisplay(e164) {
  if (!e164 || e164.length < 12) return e164;
  const d = e164.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return e164;
}

export default function Step2CarrierDetails({ draftState }) {
  const { draft, upsertNumber, carrierByPhone, setCarrier } = draftState;
  const hydratedRef = useRef(new Set());

  // Re-hydrate carrier metadata for any number that doesn't have it cached.
  // Happens when the wizard is re-entered from the draft list (Step 1's
  // portability-check is what normally populates the map, but on re-entry the
  // numbers already exist on the draft).
  useEffect(() => {
    if (!draft) return;
    const portable = (draft.numbers ?? []).filter((n) => n?.portability?.portable === true);
    portable.forEach(async (n) => {
      const phone = n.phone_number;
      if (carrierByPhone[phone]) return;
      if (hydratedRef.current.has(phone)) return;
      hydratedRef.current.add(phone);
      try {
        const { data, error } = await lookupCarrier(phone);
        if (!error && data?.carrier) {
          setCarrier(phone, data.carrier);
        }
      } catch {
        // No-op: Step 2 still renders generic help text via UNKNOWN_FALLBACK.
      }
    });
  }, [draft, carrierByPhone, setCarrier]);

  if (!draft) return null;

  const portable = (draft.numbers ?? []).filter((n) => n?.portability?.portable === true);

  if (portable.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-8 text-center">
        Go back to Step 1 and add at least one portable number.
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-base font-semibold text-slate-900 mb-1">
        Tell us about your carrier accounts
      </h3>
      <p className="text-xs text-slate-500 mb-5">
        These details unlock the port with your current carrier. Your PIN and
        account number are encrypted in our vault and only decrypted at the
        moment we submit to your carrier.
      </p>

      <div className="space-y-5">
        {portable.map((entry) => (
          <CarrierCard
            key={entry.phone_number}
            entry={entry}
            carrier={carrierByPhone[entry.phone_number] ?? null}
            onChange={(partial) => upsertNumber(entry.phone_number, partial)}
            onEnrich={(enriched) => setCarrier(entry.phone_number, enriched)}
          />
        ))}
      </div>
    </div>
  );
}

const UNKNOWN_FALLBACK = {
  key: 'unknown',
  displayName: 'Unknown carrier',
  accountNumberHelp:
    'Look at the top of your most recent bill for the account number. It is usually labeled Account number or Customer number.',
  accountPinHelp:
    'Call your carrier and ask for a port-out PIN or transfer PIN. They can set or reset one in a few minutes.',
  supportPhone: null,
  screenshotPath: null,
  pinResetShortcut: null,
  accountNumberDigitCount: null,
  commonMistakes: [],
};

function CarrierCard({ entry, carrier, onChange, onEnrich }) {
  const meta = carrier || UNKNOWN_FALLBACK;
  const phone = entry.phone_number;
  const [showPin, setShowPin] = useState(false);
  const [pinDraft, setPinDraft] = useState('');
  const [acctDraft, setAcctDraft] = useState('');
  const [pinFocused, setPinFocused] = useState(false);
  const enrichInFlightRef = useRef(false);

  // Phase F: enrich the carrier metadata once we know the key. The Phase B
  // `lookup-carrier` response does NOT include commonMistakes / screenshotPath
  // / pinResetShortcut / accountNumberDigitCount, so we treat the absence of
  // `commonMistakes` as the marker for "rich metadata not loaded yet."
  useEffect(() => {
    if (!meta?.key || meta.key === 'unknown') return;
    if (Array.isArray(meta.commonMistakes)) return; // already enriched
    if (enrichInFlightRef.current) return;
    enrichInFlightRef.current = true;
    (async () => {
      try {
        const { data, error } = await lookupCarrierMetadata(meta.key);
        if (!error && data?.carrier && onEnrich) {
          onEnrich(data.carrier);
        }
      } catch {
        // No-op: card stays in non-enriched state and still functions.
      } finally {
        enrichInFlightRef.current = false;
      }
    })();
  }, [meta?.key, meta?.commonMistakes, onEnrich]);

  const pinIsSaved = entry.account_pin_set === true && pinDraft.length === 0;
  const acctIsSaved = entry.account_number_set === true && acctDraft.length === 0;

  // Soft-warn: PIN that matches billing ZIP is a top rejection cause.
  const pinMatchesZip =
    pinDraft.length > 0 &&
    entry.billing_zip &&
    pinDraft.replace(/\D/g, '') === String(entry.billing_zip).replace(/\D/g, '');

  // Soft-warn: account number digit count mismatch. Only fires when (a) the
  // carrier has a canonical length and (b) the user is currently typing in
  // the field (acctDraft.length > 0). We don't validate against the vaulted
  // value because that's stripped from the response.
  const digitMismatch = computeDigitMismatch(acctDraft, meta.accountNumberDigitCount);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-sm font-semibold text-slate-900">
          {formatForDisplay(phone)}
        </div>
        <div className="text-xs text-slate-500">
          {entry.losing_carrier || meta.displayName}
        </div>
      </div>

      {/* Account number */}
      <div className="mb-4">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5">
          <Hash className="w-3.5 h-3.5 text-slate-400" />
          Account number
          {acctIsSaved && (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="w-3 h-3" />
              Saved
            </span>
          )}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={acctDraft}
            onChange={(e) => setAcctDraft(e.target.value)}
            onBlur={() => {
              if (acctDraft.length > 0) {
                onChange({ account_number: acctDraft });
                setAcctDraft('');
              }
            }}
            placeholder={
              acctIsSaved
                ? '••••••• (saved, type to replace)'
                : digitCountPlaceholder(meta.accountNumberDigitCount, 'Account number from your bill')
            }
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        {digitMismatch && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>{digitMismatch}</span>
          </div>
        )}
      </div>

      {/* PIN */}
      <div className="mb-4">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5">
          <KeyRound className="w-3.5 h-3.5 text-slate-400" />
          Transfer PIN
          {pinIsSaved && (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="w-3 h-3" />
              Saved
            </span>
          )}
        </label>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type={showPin ? 'text' : 'password'}
              value={pinDraft}
              onChange={(e) => setPinDraft(e.target.value)}
              onFocus={() => setPinFocused(true)}
              onBlur={() => {
                setPinFocused(false);
                if (pinDraft.length > 0) {
                  onChange({ account_pin: pinDraft });
                  setPinDraft('');
                }
              }}
              placeholder={pinIsSaved ? '•••• (saved, type to replace)' : 'Transfer or port-out PIN'}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 pr-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPin((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded"
              aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
            >
              {showPin ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">{meta.accountPinHelp}</p>
        {pinFocused && pinMatchesZip && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>
              This PIN looks like your billing ZIP. Carriers often reject these.
              Use your carrier-issued transfer PIN if you have one.
            </span>
          </div>
        )}
      </div>

      {/* Billing ZIP */}
      <div className="mb-5">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5">
          <MapPin className="w-3.5 h-3.5 text-slate-400" />
          Billing ZIP
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={entry.billing_zip ?? ''}
          onChange={(e) => onChange({ billing_zip: e.target.value })}
          placeholder="ZIP code on the carrier bill"
          maxLength={10}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      {/* ------- Supporting reference content below the form -------
          The form fields come first so the customer can fill them out without
          scrolling past educational copy. Carrier-specific reference material
          (where to find the account number on the bill, common mistakes,
          screenshot, carrier support phone) lives below for customers who
          need to look something up. */}
      <details className="mt-2 group">
        <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 select-none inline-flex items-center gap-1">
          <Info className="w-3 h-3" />
          Need help finding these on your bill?
        </summary>

        <div className="mt-3 space-y-3">
          {/* Carrier help card */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
            <div className="flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-700 mb-0.5">Where to find this</div>
                <p>{meta.accountNumberHelp}</p>
                {meta.supportPhone && (
                  <p className="mt-2 inline-flex items-center gap-1 text-slate-700">
                    <PhoneIcon className="w-3 h-3" />
                    Carrier support: <span className="font-medium">{meta.supportPhone}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Phase F coaching panel: screenshot + PIN reset shortcut */}
          <CarrierCoachingPanel meta={meta} />

          {/* Phase F common-mistakes inline tips */}
          <CommonMistakesList items={meta.commonMistakes} />
        </div>
      </details>
    </div>
  );
}

// ============================================================================
// Phase F sub-components
// ============================================================================

function CarrierCoachingPanel({ meta }) {
  // Renders the screenshot + PIN-reset shortcut row. Only meaningful when at
  // least one of the two is populated; otherwise we return null and the
  // existing Phase B help card stays the only carrier-specific surface.
  const hasScreenshot = typeof meta?.screenshotPath === 'string' && meta.screenshotPath.length > 0;
  const hasShortcut = meta?.pinResetShortcut && (meta.pinResetShortcut.kind === 'tel' || meta.pinResetShortcut.kind === 'url');
  if (!hasScreenshot && !hasShortcut) return null;

  return (
    <div className="rounded-lg border border-orange-100 bg-orange-50/40 overflow-hidden">
      {hasScreenshot && <CarrierScreenshot src={meta.screenshotPath} carrierName={meta.displayName} />}
      {hasShortcut && (
        <div className="px-3 py-2.5 border-t border-orange-100 first:border-t-0">
          <PinResetShortcutButton shortcut={meta.pinResetShortcut} />
        </div>
      )}
    </div>
  );
}

function CarrierScreenshot({ src, carrierName }) {
  // Screenshot art is commissioned per-carrier and lives in
  // public/images/carriers/{key}/account-number.png. Until each asset lands,
  // the image 404s and we render nothing rather than a placeholder tile.
  // The text "Where to find this" help card below already explains where
  // the account number lives on the bill, so the screenshot is enrichment,
  // not a slot the customer needs to see is empty.
  const [errored, setErrored] = useState(false);
  if (errored) return null;
  return (
    <img
      src={src}
      alt={`Where to find your account number on the ${carrierName} bill`}
      onError={() => setErrored(true)}
      className="block w-full max-h-48 object-contain bg-white"
    />
  );
}

function PinResetShortcutButton({ shortcut }) {
  if (!shortcut) return null;
  if (shortcut.kind === 'tel') {
    const tel = String(shortcut.value).replace(/[^0-9*+#-]/g, '');
    return (
      <a
        href={`tel:${tel}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-700 hover:text-orange-800"
      >
        <PhoneIcon className="w-3.5 h-3.5" />
        {shortcut.label || `Call ${shortcut.value}`}
      </a>
    );
  }
  if (shortcut.kind === 'url') {
    return (
      <a
        href={shortcut.value}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-700 hover:text-orange-800"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        {shortcut.label || 'Open carrier page'}
      </a>
    );
  }
  return null;
}

function CommonMistakesList({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 mb-1.5">
        <Lightbulb className="w-3 h-3" />
        Common mistakes to avoid
      </div>
      <ul className="space-y-1">
        {items.map((tip, i) => (
          <li key={i} className="text-[11px] text-amber-900 leading-snug flex items-start gap-1.5">
            <span className="text-amber-500 mt-0.5">•</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function computeDigitMismatch(draft, expected) {
  if (!draft || draft.length === 0) return null;
  if (expected === null || expected === undefined) return null;
  const digits = draft.replace(/\D/g, '');
  if (digits.length === 0) return null;
  if (typeof expected === 'number') {
    if (digits.length === expected) return null;
    return `Expected ${expected} digits, got ${digits.length}. Double-check the bill before submitting.`;
  }
  if (Array.isArray(expected) && expected.length === 2) {
    const [min, max] = expected;
    if (digits.length >= min && digits.length <= max) return null;
    return `Expected ${min} to ${max} digits, got ${digits.length}. Double-check the bill before submitting.`;
  }
  return null;
}

function digitCountPlaceholder(expected, fallback) {
  if (expected === null || expected === undefined) return fallback;
  if (typeof expected === 'number') return `${expected}-digit account number from your bill`;
  if (Array.isArray(expected) && expected.length === 2) {
    return `${expected[0]} to ${expected[1]}-digit account number from your bill`;
  }
  return fallback;
}
